import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { useContent } from "../ContentContext";

// Only render a "leave a review" / "see more reviews" button if the business
// has configured a real Google Business Profile URL. Placeholder "#" values
// from the seed data must NOT produce a live-but-broken button that reloads
// the current page.
function isRealReviewUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (trimmed === "" || trimmed === "#") return false;
  // Must look like an http(s) link and point at a Google surface. Anything
  // else is treated as "not configured yet".
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return /google\.com|goo\.gl|g\.co|g\.page|google\.[a-z.]+/i.test(trimmed);
}

export default function Testimonials() {
  const { testimonials, company } = useContent();

  // Verified rating only — no fake fallback. If the business has not set a
  // real Google rating yet, we simply do not show a star row + score.
  const hasReviews = testimonials.length > 0;
  const avg = hasReviews
    ? (testimonials.reduce((a, t) => a + (t.rating || 0), 0) / testimonials.length).toFixed(1)
    : null;

  const reviewUrl = isRealReviewUrl(company.google) ? company.google : null;
  // The verified URL the business supplied is the "Write a review" flow
  // (…/review). To land visitors directly on the Reviews / Opinions tab of
  // the Google Business Profile — not the general profile view — swap
  // /review (singular, opens the write dialog) for /reviews (plural, opens
  // the reviews list). Both are Google's own supported g.page suffixes;
  // Google handles the redirect to the correct Maps deep-link on click.
  const seeMoreUrl = reviewUrl
    ? reviewUrl.replace(/\/review\/?$/, "/reviews")
    : null;

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 noise relative"
             style={{ background: "var(--pine)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Testimonials</Eyebrow>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]"
                style={{ color: "var(--moss)" }}>
              What our <span className="font-serif-a italic normal-case font-medium" style={{ color: "var(--brass)" }}>customers say</span>
            </h2>
          </div>
          {hasReviews && avg && (
            <div className="flex items-center gap-3" data-testid="reviews-rating">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20}
                        fill={i < Math.round(parseFloat(avg)) ? "var(--brass)" : "transparent"}
                        stroke="var(--brass)" />
                ))}
              </div>
              <span className="font-display text-3xl font-extrabold" style={{ color: "var(--moss)" }}>{avg}</span>
            </div>
          )}
        </div>

        {hasReviews ? (
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.id || i} data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="p-7 rounded-2xl border" style={{ background: "rgba(29,62,46,0.4)", borderColor: "rgba(240,235,225,0.1)" }}>
                <div className="flex mb-4">
                  {[...Array(t.rating || 5)].map((_, s) =>
                    <Star key={s} size={16} fill="var(--brass)" stroke="var(--brass)" />)}
                </div>
                <p className="font-serif-a italic text-lg leading-relaxed" style={{ color: "var(--moss)" }}>
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="font-semibold" style={{ color: "var(--moss)" }}>{t.name}</span>
                  {t.src && <span style={{ color: "var(--brass)" }}>{t.src}</span>}
                </div>
                {t.date && (
                  <span className="text-xs" style={{ color: "rgba(240,235,225,0.5)" }}>{t.date}</span>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div data-testid="reviews-empty"
               className="p-8 rounded-2xl border text-center max-w-2xl mx-auto"
               style={{ background: "rgba(29,62,46,0.4)", borderColor: "rgba(240,235,225,0.1)", color: "rgba(240,235,225,0.85)" }}>
            <p className="text-lg leading-relaxed font-serif-a italic">
              We&rsquo;re just getting our online reviews started. If we&rsquo;ve worked on your property,
              a quick Google review means the world to our small crew.
            </p>
          </div>
        )}

        {reviewUrl && (
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={seeMoreUrl || reviewUrl} target="_blank" rel="noreferrer noopener" data-testid="see-more-reviews"
               className="rounded-full px-7 py-3 font-semibold text-center border transition-colors hover:bg-white/10"
               style={{ borderColor: "rgba(240,235,225,0.4)", color: "var(--moss)" }}>
              See more reviews
            </a>
            <a href={reviewUrl} target="_blank" rel="noreferrer noopener" data-testid="leave-review"
               className="rounded-full px-7 py-3 font-semibold text-center transition-transform hover:scale-105"
               style={{ background: "var(--sun)", color: "var(--pine)" }}>
              Leave a review
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
